import { byId, isAr, formatCurrency, formatNumber, attachCopyButton, setupLiveCalculation, detectUserCountry } from './calc-common.js';
import Big from 'big.js';
import { getInvoiceTafgeet } from './invoice-currencies.js';

export const EOS_COUNTRY_CONFIGS = {
  sa: {
    countryNameAr: 'المملكة العربية السعودية',
    countryNameEn: 'Saudi Arabia',
    currency: 'SAR',
    lawNameAr: 'نظام العمل السعودي (المواد 84 و 85)',
    lawNameEn: 'Saudi Labor Law (Articles 84 & 85)',
    tier1LabelAr: 'عن الخمس سنوات الأولى (نصف شهر/سنة):',
    tier1LabelEn: 'First 5 Years Portion (0.5 mo/yr):',
    tier2LabelAr: 'عن السنوات التالية (شهر كامل/سنة):',
    tier2LabelEn: 'Subsequent Years Portion (1.0 mo/yr):',
    calc: (wage, years, reason) => {
      let firstPortion = Big(0);
      let remPortion = Big(0);
      if (years.lte(5)) {
        firstPortion = years.times(wage.times(0.5));
      } else {
        firstPortion = Big(5).times(wage.times(0.5));
        remPortion = years.minus(5).times(wage);
      }
      let base = firstPortion.plus(remPortion);
      let pct = 1.0;
      let note = isAr ? 'استحقاق كامل (100%) وفق المادة 84 من نظام العمل' : 'Full entitlement (100%) pursuant to Art. 84';

      if (reason === 'resignation') {
        if (years.lt(2)) {
          pct = 0.0;
          note = isAr ? 'لا يستحق مكافأة (الخدمة أقل من سنتين - المادة 85)' : 'No entitlement (Under 2 years - Art. 85)';
        } else if (years.lte(5)) {
          pct = 1 / 3;
          note = isAr ? 'ثلث المكافأة 33.3% (الخدمة 2 إلى 5 سنوات - المادة 85)' : '1/3 entitlement (2-5 years - Art. 85)';
        } else if (years.lt(10)) {
          pct = 2 / 3;
          note = isAr ? 'ثلثي المكافأة 66.7% (الخدمة 5 إلى 10 سنوات - المادة 85)' : '2/3 entitlement (5-10 years - Art. 85)';
        } else {
          pct = 1.0;
          note = isAr ? 'المكافأة كاملة 100% (الخدمة 10 سنوات فأكثر - المادة 85)' : 'Full 100% (10+ years - Art. 85)';
        }
      }
      return { base, firstPortion, remPortion, pct, note };
    }
  },
  ae: {
    countryNameAr: 'دولة الإمارات العربية المتحدة',
    countryNameEn: 'United Arab Emirates',
    currency: 'AED',
    lawNameAr: 'قانون العمل الإماراتي (مرسوم اتحادي رقم 33 لسنة 2021)',
    lawNameEn: 'UAE Labor Law (Federal Decree-Law No. 33/2021)',
    tier1LabelAr: 'عن السنوات الـ 5 الأولى (21 يوماً/سنة):',
    tier1LabelEn: 'First 5 Years Portion (21 days/yr):',
    tier2LabelAr: 'عن السنوات التالية (30 يوماً/سنة):',
    tier2LabelEn: 'Subsequent Years Portion (30 days/yr):',
    calc: (wage, years, reason) => {
      let firstPortion = Big(0);
      let remPortion = Big(0);
      const rate21 = wage.times(21).div(30);
      if (years.lte(5)) {
        firstPortion = years.times(rate21);
      } else {
        firstPortion = Big(5).times(rate21);
        remPortion = years.minus(5).times(wage);
      }
      let base = firstPortion.plus(remPortion);
      const maxCap = wage.times(24);
      if (base.gt(maxCap)) base = maxCap;

      let pct = years.gte(1) ? 1.0 : 0.0;
      let note = years.gte(1)
        ? (isAr ? 'استحقاق كامل 100% (المرسوم الاتحادي 33/2021 - يستحق العامل المكافأة كاملة بعد إكمال سنة)' : 'Full 100% entitlement after completing 1 year')
        : (isAr ? 'لا يستحق مكافأة (مدة الخدمة أقل من سنة كاملة)' : 'No entitlement (Service under 1 year)');

      return { base, firstPortion, remPortion, pct, note };
    }
  },
  kw: {
    countryNameAr: 'دولة الكويت',
    countryNameEn: 'Kuwait',
    currency: 'KWD',
    lawNameAr: 'قانون العمل في القطاع الأهلي الكويتي (رقم 6 لسنة 2010)',
    lawNameEn: 'Kuwait Labor Law (Law No. 6/2010)',
    tier1LabelAr: 'عن السنوات الـ 5 الأولى (15 يوماً/سنة):',
    tier1LabelEn: 'First 5 Years Portion (15 days/yr):',
    tier2LabelAr: 'عن السنوات التالية (شهر كامل/سنة):',
    tier2LabelEn: 'Subsequent Years Portion (1.0 mo/yr):',
    calc: (wage, years, reason) => {
      let firstPortion = Big(0);
      let remPortion = Big(0);
      if (years.lte(5)) {
        firstPortion = years.times(wage.times(0.5));
      } else {
        firstPortion = Big(5).times(wage.times(0.5));
        remPortion = years.minus(5).times(wage);
      }
      let base = firstPortion.plus(remPortion);
      const maxCap = wage.times(18);
      if (base.gt(maxCap)) base = maxCap;

      let pct = 1.0;
      let note = isAr ? 'استحقاق كامل (100%) وفق المادة 51' : 'Full entitlement under Art. 51';

      if (reason === 'resignation') {
        if (years.lt(3)) {
          pct = 0.0;
          note = isAr ? 'لا يستحق مكافأة نهاية الخدمة (مدة الخدمة أقل من 3 سنوات - المادة 53)' : 'No entitlement (Under 3 years - Art. 53)';
        } else if (years.lte(5)) {
          pct = 0.5;
          note = isAr ? 'نصف المكافأة 50% (مدة الخدمة من 3 إلى 5 سنوات - المادة 53)' : 'Half entitlement 50% (3-5 years - Art. 53)';
        } else if (years.lt(10)) {
          pct = 2 / 3;
          note = isAr ? 'ثلثي المكافأة 66.7% (مدة الخدمة من 5 إلى 10 سنوات - المادة 53)' : '2/3 entitlement 66.7% (5-10 years - Art. 53)';
        } else {
          pct = 1.0;
          note = isAr ? 'المكافأة كاملة 100% (مدة الخدمة 10 سنوات فأكثر - المادة 53)' : 'Full 100% entitlement (10+ years - Art. 53)';
        }
      }
      return { base, firstPortion, remPortion, pct, note };
    }
  },
  qa: {
    countryNameAr: 'دولة قطر',
    countryNameEn: 'Qatar',
    currency: 'QAR',
    lawNameAr: 'قانون العمل القطري (رقم 14 لسنة 2004 - المادة 54)',
    lawNameEn: 'Qatar Labor Law (Law No. 14/2004 - Art. 54)',
    tier1LabelAr: 'عن كامل سنوات الخدمة (3 أسابيع/سنة):',
    tier1LabelEn: 'Total Service (3 weeks wage/yr):',
    tier2LabelAr: 'الشرائح الإضافية:',
    tier2LabelEn: 'Additional Tiers:',
    calc: (wage, years, reason) => {
      const rate21 = wage.times(21).div(30);
      let base = years.times(rate21);
      let pct = years.gte(1) ? 1.0 : 0.0;
      let note = years.gte(1)
        ? (isAr ? 'أجر 3 أسابيع (21 يوماً) عن كل سنة خدمة كاملة (المادة 54)' : '3 weeks (21 days) wage per year (Art. 54)')
        : (isAr ? 'لا يستحق مكافأة (مدة الخدمة أقل من سنة كاملة)' : 'No entitlement (Service under 1 year)');
      return { base, firstPortion: base, remPortion: Big(0), pct, note };
    }
  },
  bh: {
    countryNameAr: 'مملكة البحرين',
    countryNameEn: 'Bahrain',
    currency: 'BHD',
    lawNameAr: 'قانون العمل البحريني في القطاع الأهلي (رقم 36 لسنة 2012 - المادة 116)',
    lawNameEn: 'Bahrain Labor Law (Law No. 36/2012 - Art. 116)',
    tier1LabelAr: 'عن السنوات الـ 3 الأولى (نصف شهر/سنة):',
    tier1LabelEn: 'First 3 Years Portion (0.5 mo/yr):',
    tier2LabelAr: 'عن السنوات التالية (شهر كامل/سنة):',
    tier2LabelEn: 'Subsequent Years Portion (1.0 mo/yr):',
    calc: (wage, years, reason) => {
      let firstPortion = Big(0);
      let remPortion = Big(0);
      if (years.lte(3)) {
        firstPortion = years.times(wage.times(0.5));
      } else {
        firstPortion = Big(3).times(wage.times(0.5));
        remPortion = years.minus(3).times(wage);
      }
      let base = firstPortion.plus(remPortion);
      let pct = 1.0;
      let note = isAr ? 'أجر نصف شهر عن السنوات الـ 3 الأولى وشهر عن كل سنة تالية (المادة 116)' : 'Half-month for first 3 years, 1 month thereafter (Art. 116)';
      return { base, firstPortion, remPortion, pct, note };
    }
  },
  om: {
    countryNameAr: 'سلطنة عُمان',
    countryNameEn: 'Oman',
    currency: 'OMR',
    lawNameAr: 'قانون العمل العماني الجديد (مرسوم سلطاني 53/2023 - المادة 61)',
    lawNameEn: 'Oman Labor Law (Royal Decree 53/2023 - Art. 61)',
    tier1LabelAr: 'عن كامل سنوات الخدمة (أجر شهر كامل/سنة):',
    tier1LabelEn: 'Total Service (1 full month wage/yr):',
    tier2LabelAr: 'الشرائح الإضافية:',
    tier2LabelEn: 'Additional Tiers:',
    calc: (wage, years, reason) => {
      let base = years.times(wage);
      let pct = 1.0;
      let note = isAr ? 'أجر شهر كامل عن كل سنة خدمة وفق قانون العمل العماني 53/2023' : 'One full month basic wage per year under Decree 53/2023';
      return { base, firstPortion: base, remPortion: Big(0), pct, note };
    }
  },
  eg: {
    countryNameAr: 'جمهورية مصر العربية',
    countryNameEn: 'Egypt',
    currency: 'EGP',
    lawNameAr: 'قانون العمل المصري (رقم 12 لسنة 2003 - المادة 126)',
    lawNameEn: 'Egyptian Labor Law (Law No. 12/2003 - Art. 126)',
    tier1LabelAr: 'عن السنوات الـ 5 الأولى (نصف شهر/سنة):',
    tier1LabelEn: 'First 5 Years Portion (0.5 mo/yr):',
    tier2LabelAr: 'عن السنوات التالية (شهر كامل/سنة):',
    tier2LabelEn: 'Subsequent Years Portion (1.0 mo/yr):',
    calc: (wage, years, reason) => {
      let firstPortion = Big(0);
      let remPortion = Big(0);
      if (years.lte(5)) {
        firstPortion = years.times(wage.times(0.5));
      } else {
        firstPortion = Big(5).times(wage.times(0.5));
        remPortion = years.minus(5).times(wage);
      }
      let base = firstPortion.plus(remPortion);
      let pct = 1.0;
      let note = isAr ? 'أجر نصف شهر عن الخمس سنوات الأولى وشهر عن السنوات التالية (المادة 126)' : 'Half month for first 5 years, 1 month thereafter (Art. 126)';
      return { base, firstPortion, remPortion, pct, note };
    }
  },
  jo: {
    countryNameAr: 'المملكة الأردنية الهاشمية',
    countryNameEn: 'Jordan',
    currency: 'JOD',
    lawNameAr: 'قانون العمل الأردني (رقم 8 لسنة 1996 - المادة 32)',
    lawNameEn: 'Jordanian Labor Law (Law No. 8/1996 - Art. 32)',
    tier1LabelAr: 'عن كامل سنوات الخدمة (أجر شهر كامل/سنة):',
    tier1LabelEn: 'Total Service (1 month wage/yr):',
    tier2LabelAr: 'الشرائح الإضافية:',
    tier2LabelEn: 'Additional Tiers:',
    calc: (wage, years, reason) => {
      let base = years.times(wage);
      let pct = 1.0;
      let note = isAr ? 'أجر شهر كامل عن كل سنة خدمة فعلية (المادة 32 من قانون العمل الأردني)' : 'One month wage per year of actual service (Art. 32)';
      return { base, firstPortion: base, remPortion: Big(0), pct, note };
    }
  },
  ma: {
    countryNameAr: 'المملكة المغربية',
    countryNameEn: 'Morocco',
    currency: 'MAD',
    lawNameAr: 'مدونة الشغل المغربية (المادتان 414 و 415 - تعويض الفصل)',
    lawNameEn: 'Moroccan Labor Code (Articles 414 & 415)',
    tier1LabelAr: 'عن السنوات الـ 5 الأولى (96 ساعة أجر/سنة):',
    tier1LabelEn: 'First 5 Years Portion (96 hrs wage/yr):',
    tier2LabelAr: 'عن السنوات التالية (144 إلى 240 ساعة/سنة):',
    tier2LabelEn: 'Subsequent Years Portion (144-240 hrs/yr):',
    calc: (wage, years, reason) => {
      // 191 standard monthly hours in Moroccan labor law
      const hourlyWage = wage.gt(0) ? wage.div(191) : Big(0);
      let firstPortion = Big(0);
      let remPortion = Big(0);

      const y = years.toNumber();
      if (y <= 5) {
        firstPortion = hourlyWage.times(96).times(years);
      } else {
        firstPortion = hourlyWage.times(96).times(5);
        if (y <= 10) {
          remPortion = hourlyWage.times(144).times(years.minus(5));
        } else if (y <= 15) {
          remPortion = hourlyWage.times(144).times(5).plus(hourlyWage.times(192).times(years.minus(10)));
        } else {
          remPortion = hourlyWage.times(144).times(5)
            .plus(hourlyWage.times(192).times(5))
            .plus(hourlyWage.times(240).times(years.minus(15)));
        }
      }
      let base = firstPortion.plus(remPortion);
      let pct = years.gte(0.5) ? 1.0 : 0.0;
      let note = isAr
        ? 'احتساب تعويض نهاية الخدمة / الفصل وفق مدونة الشغل المغربية (المواد 414-415)'
        : 'Severance compensation calculated under Moroccan Labor Code Arts. 414-415';
      return { base, firstPortion, remPortion, pct, note };
    }
  },
  dz: {
    countryNameAr: 'الجمهورية الجزائرية الديمقراطية الشعبية',
    countryNameEn: 'Algeria',
    currency: 'DZD',
    lawNameAr: 'قانون علاقات العمل الجزائري (قانون 90-11)',
    lawNameEn: 'Algerian Labor Relations Law (Law 90-11)',
    tier1LabelAr: 'عن كامل سنوات الخدمة (أجر شهر/سنة):',
    tier1LabelEn: 'Total Service (1 month wage/yr):',
    tier2LabelAr: 'الشرائح الإضافية:',
    tier2LabelEn: 'Additional Tiers:',
    calc: (wage, years, reason) => {
      let base = years.times(wage);
      let pct = 1.0;
      let note = isAr
        ? 'تعويض نهاية الخدمة / التسريح بمعدل أجر شهر عن كل سنة خدمة (قانون 90-11)'
        : 'One month wage per year of service pursuant to Law 90-11';
      return { base, firstPortion: base, remPortion: Big(0), pct, note };
    }
  },
  tn: {
    countryNameAr: 'الجمهورية التونسية',
    countryNameEn: 'Tunisia',
    currency: 'TND',
    lawNameAr: 'مجلة الشغل التونسية (الفصل 22 مكرر)',
    lawNameEn: 'Tunisian Labor Code (Article 22 bis)',
    tier1LabelAr: 'مكافأة نهاية الخدمة (أجر يوم عن كل شهر خدمة):',
    tier1LabelEn: 'Gratuity (1 day wage per month of service):',
    tier2LabelAr: 'الحد الأقصى القانوني:',
    tier2LabelEn: 'Statutory Maximum:',
    calc: (wage, years, reason) => {
      // 1 day's wage per month of service, max 3 months wage
      const totalMonths = years.times(12);
      const dailyWage = wage.div(26);
      let base = totalMonths.times(dailyWage);
      const maxCap = wage.times(3);
      if (base.gt(maxCap)) base = maxCap;
      let pct = 1.0;
      let note = isAr
        ? 'مكافأة نهاية الخدمة طبقاً لمجلة الشغل التونسية (بحد أقصى أجر 3 أشهر)'
        : 'Gratuity pursuant to Tunisian Labor Code (capped at 3 months wage)';
      return { base, firstPortion: base, remPortion: Big(0), pct, note };
    }
  },
  iq: {
    countryNameAr: 'جمهورية العراق',
    countryNameEn: 'Iraq',
    currency: 'IQD',
    lawNameAr: 'قانون العمل العراقي (رقم 37 لسنة 2015 - المادة 45)',
    lawNameEn: 'Iraqi Labor Law (Law No. 37/2015 - Art. 45)',
    tier1LabelAr: 'عن السنوات الـ 5 الأولى (أجر أسبوعين/سنة):',
    tier1LabelEn: 'First 5 Years Portion (2 weeks wage/yr):',
    tier2LabelAr: 'عن السنوات التالية (أجر شهر كامل/سنة):',
    tier2LabelEn: 'Subsequent Years Portion (1 month wage/yr):',
    calc: (wage, years, reason) => {
      const twoWeeksRate = wage.times(14).div(30);
      let firstPortion = Big(0);
      let remPortion = Big(0);
      if (years.lte(5)) {
        firstPortion = years.times(twoWeeksRate);
      } else {
        firstPortion = Big(5).times(twoWeeksRate);
        remPortion = years.minus(5).times(wage);
      }
      let base = firstPortion.plus(remPortion);
      let pct = 1.0;
      let note = isAr
        ? 'أجر أسبوعين عن كل سنة للسنوات الخمس الأولى وشهر عن السنوات التالية (المادة 45)'
        : 'Two weeks wage for first 5 yrs, one month thereafter (Art. 45)';
      return { base, firstPortion, remPortion, pct, note };
    }
  },
  lb: {
    countryNameAr: 'الجمهورية اللبنانية',
    countryNameEn: 'Lebanon',
    currency: 'LBP',
    lawNameAr: 'قانون العمل اللبناني (المادة 54)',
    lawNameEn: 'Lebanese Labor Law (Article 54)',
    tier1LabelAr: 'عن كامل سنوات الخدمة (أجر شهر كامل/سنة):',
    tier1LabelEn: 'Total Service (1 month wage/yr):',
    tier2LabelAr: 'الشرائح الإضافية:',
    tier2LabelEn: 'Additional Tiers:',
    calc: (wage, years, reason) => {
      let base = years.times(wage);
      let pct = 1.0;
      let note = isAr ? 'أجر شهر عن كل سنة خدمة وفق المادة 54 من قانون العمل اللبناني' : 'One month wage per year of service under Lebanese Labor Law Art. 54';
      return { base, firstPortion: base, remPortion: Big(0), pct, note };
    }
  },
  ly: {
    countryNameAr: 'دولة ليبيا',
    countryNameEn: 'Libya',
    currency: 'LYD',
    lawNameAr: 'قانون علاقات العمل الليبي (رقم 12 لسنة 2010)',
    lawNameEn: 'Libyan Labor Relations Law (Law No. 12/2010)',
    tier1LabelAr: 'عن كامل سنوات الخدمة (أجر شهر كامل/سنة):',
    tier1LabelEn: 'Total Service (1 month wage/yr):',
    tier2LabelAr: 'الشرائح الإضافية:',
    tier2LabelEn: 'Additional Tiers:',
    calc: (wage, years, reason) => {
      let base = years.times(wage);
      let pct = 1.0;
      let note = isAr ? 'أجر شهر عن كل سنة خدمة بموجب قانون علاقات العمل الليبي' : 'One month wage per year of service under Libyan Labor Law';
      return { base, firstPortion: base, remPortion: Big(0), pct, note };
    }
  },
  ye: {
    countryNameAr: 'الجمهورية اليمنية',
    countryNameEn: 'Yemen',
    currency: 'YER',
    lawNameAr: 'قانون العمل اليمني (رقم 5 لسنة 1995 - المادة 120)',
    lawNameEn: 'Yemeni Labor Law (Law No. 5/1995 - Art. 120)',
    tier1LabelAr: 'عن كامل سنوات الخدمة (أجر شهر كامل/سنة):',
    tier1LabelEn: 'Total Service (1 month wage/yr):',
    tier2LabelAr: 'الشرائح الإضافية:',
    tier2LabelEn: 'Additional Tiers:',
    calc: (wage, years, reason) => {
      let base = years.times(wage);
      let pct = 1.0;
      let note = isAr ? 'أجر شهر كامل عن كل سنة خدمة فعلية (المادة 120)' : 'One month wage per year of service (Art. 120)';
      return { base, firstPortion: base, remPortion: Big(0), pct, note };
    }
  },
  sd: {
    countryNameAr: 'جمهورية السودان',
    countryNameEn: 'Sudan',
    currency: 'SDG',
    lawNameAr: 'قانون العمل السوداني لسنة 1997',
    lawNameEn: 'Sudanese Labor Act 1997',
    tier1LabelAr: 'عن كامل سنوات الخدمة (أجر شهر كامل/سنة):',
    tier1LabelEn: 'Total Service (1 month wage/yr):',
    tier2LabelAr: 'الشرائح الإضافية:',
    tier2LabelEn: 'Additional Tiers:',
    calc: (wage, years, reason) => {
      let base = years.times(wage);
      let pct = 1.0;
      let note = isAr ? 'أجر شهر عن كل سنة خدمة بموجب قانون العمل السوداني' : 'One month wage per year of service under Sudanese Labor Act';
      return { base, firstPortion: base, remPortion: Big(0), pct, note };
    }
  }
};

export function calculateEndOfService({
  wage,
  years,
  months = 0,
  days = 0,
  reason = 'employer_termination',
  vacationDays = 0,
  country = 'sa'
}) {
  const totalYears = Big(years).plus(Big(months).div(12)).plus(Big(days).div(365));
  const monthlyWage = Big(wage);

  const cfg = EOS_COUNTRY_CONFIGS[country] || EOS_COUNTRY_CONFIGS.sa;
  const { base, firstPortion, remPortion, pct, note } = cfg.calc(monthlyWage, totalYears, reason);

  let percentage = pct;
  let reasonNote = note;

  if (reason === 'force_majeure' || reason === 'article_81' || reason === 'female_marriage') {
    percentage = 1.0;
    reasonNote = isAr
      ? 'استحقاق كامل 100% بموجب الاستثناءات النظامية لإنهاء العمل'
      : 'Full 100% entitlement under legal exceptions';
  }

  const finalBenefit = base.times(percentage);

  // Unused vacation compensation: (monthlyWage / 30) * vacationDays
  const dailyWage = monthlyWage.gt(0) ? monthlyWage.div(30) : Big(0);
  const vacationPay = dailyWage.times(Big(vacationDays || 0));
  const totalWithVacation = finalBenefit.plus(vacationPay);

  return {
    totalYears: Number(totalYears.toFixed(3)),
    baseReward: Number(base.toFixed(2)),
    percentage,
    finalBenefit: Number(finalBenefit.toFixed(2)),
    first5YearsPortion: Number(firstPortion.toFixed(2)),
    remainingYearsPortion: Number(remPortion.toFixed(2)),
    vacationPay: Number(vacationPay.toFixed(2)),
    totalWithVacation: Number(totalWithVacation.toFixed(2)),
    reasonNote,
    currency: cfg.currency,
    tier1Label: isAr ? cfg.tier1LabelAr : cfg.tier1LabelEn,
    tier2Label: isAr ? cfg.tier2LabelAr : cfg.tier2LabelEn,
    lawName: isAr ? cfg.lawNameAr : cfg.lawNameEn
  };
}

// Backwards compatibility alias
export const calculateSaudiEndOfService = calculateEndOfService;

export function initEndOfService() {
  const container = byId('eos-calculator-app');
  if (!container) return;

  const countrySelect = byId('eos-country');
  const currencySelect = byId('eos-currency');

  // Automatic country & currency detection with Saudi Arabia (SA / SAR) fallback
  const detectedCountry = (detectUserCountry() || 'SA').toLowerCase();
  const initialCountry = EOS_COUNTRY_CONFIGS[detectedCountry] ? detectedCountry : 'sa';
  if (countrySelect) {
    countrySelect.value = initialCountry;
  }
  if (currencySelect) {
    currencySelect.value = EOS_COUNTRY_CONFIGS[initialCountry]?.currency || 'SAR';
  }

  if (countrySelect) {
    countrySelect.addEventListener('change', (e) => {
      const c = e.target.value;
      if (currencySelect && EOS_COUNTRY_CONFIGS[c]?.currency) {
        currencySelect.value = EOS_COUNTRY_CONFIGS[c].currency;
      }
      runEosCalc();
    });
  }

  const modeRadios = document.querySelectorAll('input[name="eos-input-mode"]');
  const durationInputs = byId('eos-duration-inputs');
  const dateInputs = byId('eos-date-inputs');

  modeRadios.forEach((radio) => {
    radio.addEventListener('change', () => {
      if (radio.value === 'dates' && radio.checked) {
        if (durationInputs) durationInputs.style.display = 'none';
        if (dateInputs) dateInputs.style.display = 'grid';
      } else if (radio.value === 'duration' && radio.checked) {
        if (durationInputs) durationInputs.style.display = 'grid';
        if (dateInputs) dateInputs.style.display = 'none';
      }
      runEosCalc();
    });
  });

  const startDateInp = byId('eos-start-date');
  const endDateInp = byId('eos-end-date');

  function updateDurationFromDates() {
    if (!startDateInp?.value || !endDateInp?.value) return;
    const start = new Date(startDateInp.value);
    const end = new Date(endDateInp.value);
    if (end < start) return;

    let y = end.getFullYear() - start.getFullYear();
    let m = end.getMonth() - start.getMonth();
    let d = end.getDate() - start.getDate();

    if (d < 0) {
      m -= 1;
      const prevMonthLastDay = new Date(end.getFullYear(), end.getMonth(), 0).getDate();
      d += prevMonthLastDay;
    }
    if (m < 0) {
      y -= 1;
      m += 12;
    }

    if (byId('eos-years')) byId('eos-years').value = Math.max(0, y);
    if (byId('eos-months')) byId('eos-months').value = Math.max(0, m);
    if (byId('eos-days')) byId('eos-days').value = Math.max(0, d);
  }

  [startDateInp, endDateInp].forEach((el) => {
    if (el) {
      el.addEventListener('change', () => {
        updateDurationFromDates();
        runEosCalc();
      });
    }
  });

  setupLiveCalculation(
    [
      'eos-country',
      'eos-currency',
      'eos-wage',
      'eos-years',
      'eos-months',
      'eos-days',
      'eos-reason',
      'eos-contract-type',
      'eos-vacation-days'
    ],
    runEosCalc
  );

  attachCopyButton(byId('eos-copy-btn'), () => {
    const total = byId('eos-final-result')?.textContent || '';
    const fullTotal = byId('eos-total-with-leave')?.textContent || '';
    const note = byId('eos-reason-note')?.textContent || '';
    return isAr
      ? `📋 مستحقات مكافأة نهاية الخدمة:\n• مكافأة نهاية الخدمة: ${total}\n• الإجمالي شاملاً رصيد الإجازات: ${fullTotal}\n• ${note}`
      : `📋 End of Service Benefits Summary:\n• Gratuity: ${total}\n• Total with Vacation: ${fullTotal}\n• ${note}`;
  });

  const printBtn = byId('eos-print-btn');
  if (printBtn) {
    printBtn.addEventListener('click', () => {
      window.print();
    });
  }

  runEosCalc();
}

// Backwards compatibility alias
export const initSaudiEndOfService = initEndOfService;

function runEosCalc() {
  const wage = parseFloat(byId('eos-wage')?.value) || 0;
  const years = parseFloat(byId('eos-years')?.value) || 0;
  const months = parseFloat(byId('eos-months')?.value) || 0;
  const days = parseFloat(byId('eos-days')?.value) || 0;
  const reason = byId('eos-reason')?.value || 'employer_termination';
  const vacationDays = parseFloat(byId('eos-vacation-days')?.value) || 0;
  const country = byId('eos-country')?.value || 'sa';
  const currency = byId('eos-currency')?.value || EOS_COUNTRY_CONFIGS[country]?.currency || 'SAR';

  const cfg = EOS_COUNTRY_CONFIGS[country] || EOS_COUNTRY_CONFIGS.sa;

  // Update dynamic labels according to selected country
  if (byId('eos-tier1-label')) {
    byId('eos-tier1-label').textContent = isAr ? cfg.tier1LabelAr : cfg.tier1LabelEn;
  }
  if (byId('eos-tier2-label')) {
    byId('eos-tier2-label').textContent = isAr ? cfg.tier2LabelAr : cfg.tier2LabelEn;
  }
  if (byId('eos-law-name')) {
    byId('eos-law-name').textContent = isAr ? cfg.lawNameAr : cfg.lawNameEn;
  }

  if (wage <= 0 || (years === 0 && months === 0 && days === 0)) {
    if (byId('eos-final-result')) byId('eos-final-result').textContent = `0.00 ${currency}`;
    if (byId('eos-base-reward')) byId('eos-base-reward').textContent = `0.00 ${currency}`;
    if (byId('eos-percent-badge')) byId('eos-percent-badge').textContent = '—';
    if (byId('eos-first5-val')) byId('eos-first5-val').textContent = `0.00 ${currency}`;
    if (byId('eos-rem-val')) byId('eos-rem-val').textContent = `0.00 ${currency}`;
    if (byId('eos-vacation-val')) byId('eos-vacation-val').textContent = `0.00 ${currency}`;
    if (byId('eos-total-with-leave')) byId('eos-total-with-leave').textContent = `0.00 ${currency}`;
    return;
  }

  const result = calculateEndOfService({ wage, years, months, days, reason, vacationDays, country });

  if (byId('eos-final-result')) byId('eos-final-result').textContent = formatCurrency(result.finalBenefit, currency);
  if (byId('eos-base-reward')) byId('eos-base-reward').textContent = formatCurrency(result.baseReward, currency);
  if (byId('eos-percent-badge')) byId('eos-percent-badge').textContent = `${Math.round(result.percentage * 100)}%`;
  if (byId('eos-reason-note')) byId('eos-reason-note').textContent = result.reasonNote;
  if (byId('eos-first5-val')) byId('eos-first5-val').textContent = formatCurrency(result.first5YearsPortion, currency);
  if (byId('eos-rem-val')) byId('eos-rem-val').textContent = formatCurrency(result.remainingYearsPortion, currency);
  if (byId('eos-vacation-val')) byId('eos-vacation-val').textContent = formatCurrency(result.vacationPay, currency);
  if (byId('eos-total-with-leave')) byId('eos-total-with-leave').textContent = formatCurrency(result.totalWithVacation, currency);

  const tafgeetEl = byId('eos-tafgeet-val');
  if (tafgeetEl) {
    tafgeetEl.textContent = getInvoiceTafgeet(result.totalWithVacation.toFixed(2), currency);
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initEndOfService);
  } else {
    initEndOfService();
  }
}
